/*
 * SofterHour storefront helpers.
 * 1. One-tap add-on forms (cart drawer and "Add to your routine" rows) that use the same
 *    cart events as Horizon's product form, so the drawer, the cart bubble and the
 *    free-delivery bar refresh themselves.
 * 2. The estimated delivery date range under the buy buttons.
 * 3. Selecting the preferred pack (the pair) when a visitor arrives without a chosen variant.
 * 4. The sort menu on collection pages, which reloads the page as soon as a choice is made.
 *    Product cards with one variant use the add-on form handler in part 1.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/* ---------- 1. Add-on forms ---------- */

function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

function rootUrl() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return root || '/';
}

/** @param {HTMLFormElement} form */
async function addFromForm(form) {
  const button = form.querySelector('button[type="submit"]');
  const status = form.querySelector('[data-sh-add-status]');
  const idField = form.elements.namedItem('id');
  const variantId = idField && 'value' in idField ? String(idField.value) : '';
  if (!variantId || form.dataset.busy === 'true') return;

  form.dataset.busy = 'true';
  if (button) {
    button.setAttribute('aria-disabled', 'true');
    button.dataset.label = button.dataset.label || button.textContent.trim();
    button.textContent = button.dataset.adding || 'Adding';
  }
  if (status) status.textContent = '';

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const sections = cartSectionIds();
    const response = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    const cart = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'This item could not be added.';
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'softer-hour-add-on' },
      });
      form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      if (status) status.textContent = message;
      if (button) button.textContent = button.dataset.label;
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        itemCount: cart.item_count,
        sections: data.sections,
        source: 'softer-hour-add-on',
      },
    });
    if (button) button.textContent = button.dataset.added || 'Added';
    if (status) status.textContent = 'Added to cart';
  } catch (error) {
    deferred.reject(error);
    if (status) status.textContent = 'Something went wrong. Please try again.';
    if (button) button.textContent = button.dataset.label;
  } finally {
    form.dataset.busy = 'false';
    if (button) {
      button.removeAttribute('aria-disabled');
      window.setTimeout(() => {
        if (button.isConnected && button.dataset.label) button.textContent = button.dataset.label;
        if (status && status.isConnected && status.textContent === 'Added to cart') status.textContent = '';
      }, 2000);
    }
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-sh-add-form]')) return;
  event.preventDefault();
  addFromForm(form);
});

/*
 * A colour that only exists in another pack (for example a single-only colour while the pair is
 * chosen) carries data-sh-fallback-values with the option value IDs to use instead. Check those
 * before Horizon's variant picker reads the selection, so the request names a real variant.
 */
document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.shFallbackValues) return;
    const picker = input.closest('variant-picker');
    if (!picker) return;
    for (const id of input.dataset.shFallbackValues.split(',')) {
      if (!id) continue;
      const other = picker.querySelector(`input[data-option-value-id="${CSS.escape(id)}"]`);
      if (other instanceof HTMLInputElement && other !== input) other.checked = true;
    }
  },
  true
);

/* ---------- 2. Delivery estimate ---------- */

const ukDate = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** Midday today in London, as a Date, so adding whole days never trips over a clock change. */
function londonMidday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const [y, m, d] = parts.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.minDays || 6);
    const max = Number(this.dataset.maxDays || 12);
    const from = this.querySelector('[data-sh-date-from]');
    const to = this.querySelector('[data-sh-date-to]');
    if (!from || !to) return;
    const base = londonMidday().getTime();
    const day = 24 * 60 * 60 * 1000;
    from.textContent = ukDate.format(new Date(base + min * day));
    to.textContent = ukDate.format(new Date(base + max * day));
  }
}

if (!customElements.get('sh-delivery-estimate')) {
  customElements.define('sh-delivery-estimate', DeliveryEstimate);
}

/* ---------- 3. Preferred pack ---------- */

async function selectPreferredPacks() {
  const fieldsets = document.querySelectorAll('fieldset[data-sh-default-value]');
  if (!fieldsets.length) return;
  await customElements.whenDefined('variant-picker');

  const hasVariantParam = new URL(window.location.href).searchParams.has('variant');

  for (const fieldset of fieldsets) {
    if (fieldset.closest('product-card, quick-add-dialog, dialog')) continue;
    const picker = fieldset.closest('variant-picker');
    if (!picker) continue;
    const isMainProduct = picker.dataset.templateProductMatch === 'true';
    if (isMainProduct && hasVariantParam) continue;

    const wanted = (fieldset.dataset.shDefaultValue || '').trim().toLowerCase();
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const target = inputs.find((input) => input.value.trim().toLowerCase() === wanted);
    if (!target || target.checked || target.dataset.optionAvailable !== 'true') continue;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', selectPreferredPacks, { once: true });
} else {
  selectPreferredPacks();
}

/* ---------- 4. Collection sort ---------- */

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-sh-autosubmit]')) return;
  const form = select.form;
  if (!form) return;
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.submit();
});
